import { flexRender } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import * as React from 'react';
import { DataGridColumnHeader } from 'src/shared/components/data-grid/data-grid-column-header';
import { DataGridRow } from 'src/shared/components/data-grid/data-grid-row';
import type { useDataGrid } from 'src/shared/hooks/use-data-grid';
import { cn } from 'src/shared/lib/utils';

interface DataGridProps<TData> extends ReturnType<typeof useDataGrid<TData>>, React.ComponentProps<'div'> {
  height?: number;
  headerIcons?: boolean;
}

export function DataGrid<TData>({
  dataGridRef,
  headerRef,
  rowMapRef,
  footerRef,
  table,
  rowVirtualizer,
  columnSizeVars,
  onRowAdd,
  headerIcons = false,
  className,
  ...props
}: DataGridProps<TData>) {
  const rows = table.getRowModel().rows;
  const columns = table.getAllColumns();

  const meta = table.options.meta;
  const rowHeight = meta?.rowHeight ?? 'short';
  const focusedCell = meta?.focusedCell ?? null;

  const onAddRowKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!onRowAdd) return;

      if (event.key === 'Enter' || event.key === '') {
        event.preventDefault();
        onRowAdd();
      }
    },
    [onRowAdd]
  );

  return (
    <div data-slot="grid-wrapper" className={cn('relative flex w-full flex-col', className)} {...props}>
      <div
        role="grid"
        aria-label="Data grid"
        aria-rowcount={rows.length + (onRowAdd ? 1 : 0)}
        aria-colcount={columns.length}
        data-slot="grid"
        tabIndex={0}
        ref={dataGridRef}
        className="relative grid select-none overflow-auto scrollbar-gutter rounded-md border border-neutral-200 focus:outline-none dark:border-neutral-800"
        style={{
          ...columnSizeVars,
          maxHeight: 'calc(100vh - 200px)',
        }}
      >
        <div
          role="rowgroup"
          data-slot="grid-header"
          ref={headerRef}
          className="sticky top-0 z-10 grid border-b bg-neutral-100 dark:bg-neutral-800"
        >
          {table.getHeaderGroups().map((headerGroup, rowIndex) => (
            <div
              key={headerGroup.id}
              role="row"
              aria-rowindex={rowIndex + 1}
              data-slot="grid-header-row"
              tabIndex={-1}
              className="flex w-full"
            >
              {headerGroup.headers.map((header, colIndex) => {
                const sorting = table.getState().sorting;
                const currentSort = sorting.find((sort) => sort.id === header.column.id);
                const isSortable = header.column.getCanSort();

                return (
                  <div
                    key={header.id}
                    role="columnheader"
                    aria-colindex={colIndex + 1}
                    aria-sort={
                      currentSort?.desc === false
                        ? 'ascending'
                        : currentSort?.desc === true
                          ? 'descending'
                          : isSortable
                            ? 'none'
                            : undefined
                    }
                    data-slot="grid-header-cell"
                    tabIndex={-1}
                    className={cn('relative', {
                      'border-r': header.column.id !== 'select',
                    })}
                    style={{
                      width: `calc(var(--header-${header.id}-size) * 1px)`,
                    }}
                  >
                    {header.isPlaceholder ? null : typeof header.column.columnDef.header === 'function' ? (
                      <div className="size-full px-3 py-1.5">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    ) : (
                      <DataGridColumnHeader header={header} table={table} headerIcons={headerIcons} />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div
          role="rowgroup"
          data-slot="grid-body"
          className="relative grid"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
          }}
        >
          {rowVirtualizer.getVirtualIndexes().map((virtualRowIndex) => {
            const row = rows[virtualRowIndex];
            if (!row) return null;

            return (
              <DataGridRow
                key={row.id}
                row={row}
                table={table}
                rowMapRef={rowMapRef}
                virtualRowIndex={virtualRowIndex}
                rowVirtualizer={rowVirtualizer}
                rowHeight={rowHeight}
                focusedCell={focusedCell}
              />
            );
          })}
        </div>
        {onRowAdd && (
          <div
            role="rowgroup"
            data-slot="grid-footer"
            ref={footerRef}
            className="sticky bottom-0 z-10 grid border-t bg-white dark:bg-neutral-950"
          >
            <div
              role="row"
              aria-rowindex={rows.length + 2}
              data-slot="grid-add-row"
              tabIndex={-1}
              className="flex w-full"
            >
              <div
                role="gridcell"
                tabIndex={0}
                className="relative flex h-9 grow items-center bg-white transition-colors hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none cursor-pointer dark:bg-neutral-950 dark:hover:bg-neutral-900 dark:focus:bg-neutral-900"
                style={{
                  width: table.getTotalSize(),
                  minWidth: table.getTotalSize(),
                }}
                onClick={onRowAdd}
                onKeyDown={onAddRowKeyDown}
              >
                <div className="sticky left-0 flex items-center gap-2 px-3 text-neutral-500 dark:text-neutral-400">
                  <Plus className="size-3.5" />
                  <span className="text-sm">Add row</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
